import * as React from 'react'

import { Controller, useFormContext, type RegisterOptions } from 'react-hook-form'

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { cn } from '@/lib/utils'

import { controlVariants, type ControlSize } from './control-styles'
import { useControlName } from './lib/use-control-name'

/**
 * Seleção sobre o primitivo Radix.
 *
 * O controle anterior era um `<select>` nativo. A troca é deliberada e tem um
 * custo assumido: em celular, o nativo abre o seletor do sistema operacional,
 * que é familiar e não precisa de código. O Radix desenha uma lista própria.
 *
 * O que se ganha em troca: aparência e teclado idênticos em toda a plataforma,
 * estado desabilitado e foco consistentes com os demais controles, e a mesma
 * altura de linha dos outros campos — o nativo não aceitava os tokens de
 * controle sem gambiarra de `appearance`.
 *
 * Aceita os dois modos dos demais controles de escolha: com `value`/`onChange`
 * opera controlado; sem eles, registra-se pelo `name`.
 */

export type SelectOption = {
  value: string
  label: React.ReactNode
  disabled?: boolean
}

type SelectPresentationProps = {
  value: string
  onValueChange: (value: string) => void
  options: SelectOption[]
  placeholder?: string
  disabled?: boolean
  size?: ControlSize
  className?: string
  /**
   * Oferece uma entrada para desfazer a escolha, em campo não obrigatório.
   *
   * O Radix não aceita `SelectItem` com `value=""` nem traz controle de limpar.
   * A saída é um valor-sentinela, traduzido para string vazia antes de chegar
   * ao formulário — quem consome nunca o vê.
   */
  isClearable?: boolean
  clearLabel?: string
  'aria-label'?: string
}

/** Improvável em qualquer lista de opções vinda do produtor. */
const CLEAR_VALUE = '__select-field-clear__'

function SelectPresentation({
  value,
  onValueChange,
  options,
  placeholder = 'Selecione',
  isClearable = false,
  clearLabel = 'Limpar seleção',
  disabled,
  size,
  className,
  ...rest
}: SelectPresentationProps) {
  return (
    // `value` sempre string, nunca `undefined`: com `undefined` o Radix nasce
    // não controlado e passa a ignorar mudanças externas — um `reset()` ou
    // `setValue()` limpava o formulário e o gatilho continuava mostrando a
    // escolha anterior. O placeholder aparece com string vazia.
    <Select
      value={value ?? ''}
      onValueChange={next => onValueChange(next === CLEAR_VALUE ? '' : next)}
      disabled={disabled}
    >
      <SelectTrigger
        className={cn(controlVariants({ size }), 'justify-between', className)}
        {...rest}
      >
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {/* Só com algo escolhido: "limpar" num campo vazio é uma linha a mais
            para ler que não faz nada. */}
        {isClearable && value ? (
          <SelectItem value={CLEAR_VALUE}>{clearLabel}</SelectItem>
        ) : null}
        {options.map(option => (
          <SelectItem key={option.value} value={option.value} disabled={option.disabled}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

type SelectFieldProps = Omit<
  SelectPresentationProps,
  'value' | 'onValueChange'
> & {
  /** Só é necessário fora de `<Field>` — ver `useControlName`. */
  name?: string
  value?: string
  onChange?: (value: string) => void
  rules?: RegisterOptions
}

export const SelectField = Object.assign(
  function SelectField({ name, value, onChange, rules, ...rest }: SelectFieldProps) {
    if (onChange) {
      return <SelectPresentation value={value ?? ''} onValueChange={onChange} {...rest} />
    }

    return <RegisteredSelect name={name} rules={rules} {...rest} />
  },
  { displayName: 'SelectField' }
)

function RegisteredSelect({
  name: nameProp,
  rules,
  ...rest
}: Omit<SelectFieldProps, 'value' | 'onChange'>) {
  const name = useControlName(nameProp)
  const { control } = useFormContext()

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field }) => (
        <SelectPresentation
          value={(field.value as string) ?? ''}
          onValueChange={field.onChange}
          {...rest}
        />
      )}
    />
  )
}
